import { useState, useEffect, useRef, useCallback } from 'react';
import { Ticket, Play, Square, Clock, Trophy, RotateCcw, Users } from 'lucide-react';
import { Event, updateEvent } from '../../services/events';
import { drawWinner, subscribeToDJLotteryRounds, LotteryRound } from '../../services/lottery';
import type { PrizeType } from '../../services/prizes';
import toast from 'react-hot-toast';
import { attendeeDisplayName } from '../../utils/attendeeDisplayName';

interface LotteryPanelProps {
  eventId: string;
  djId: string;
  event: Event;
  setEvent: React.Dispatch<React.SetStateAction<Event | null>>;
}

const PRIZE_OPTIONS: { type: PrizeType; label: string; icon: string }[] = [
  { type: 'pick_next_song', label: 'Pick Next Song', icon: '🎵' },
  { type: 'auto_queue_bypass', label: 'Queue Bypass', icon: '⚡' },
  { type: 'shoutout', label: 'Shoutout', icon: '📣' },
  { type: 'custom', label: 'Custom', icon: '🎁' },
];

function lotteryWinnerLabel(round: LotteryRound): string {
  return round.winnerId ? attendeeDisplayName(round.winnerNickname, round.winnerId) : 'Unknown Attendee';
}

export default function LotteryPanel({ eventId, djId, event, setEvent }: LotteryPanelProps) {
  const [mode, setMode] = useState<'one_time' | 'recurring'>(event.settings.lotteryMode || 'one_time');
  const [intervalMinutes, setIntervalMinutes] = useState(event.settings.lotteryIntervalMinutes || 10);
  const [prizeType, setPrizeType] = useState<PrizeType>(event.settings.lotteryPrizeType || 'shoutout');
  const [customText, setCustomText] = useState(event.settings.lotteryPrizeCustomText || '');
  const [allowRepeatWinners, setAllowRepeatWinners] = useState(event.settings.lotteryAllowRepeatWinners ?? true);

  const [isRunning, setIsRunning] = useState(event.settings.lotteryEnabled || false);
  const [drawing, setDrawing] = useState(false);
  const [rounds, setRounds] = useState<LotteryRound[]>([]);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  const nextDrawTimeRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const unsub = subscribeToDJLotteryRounds(eventId, setRounds);
    return () => unsub();
  }, [eventId]);

  const handleDraw = useCallback(async () => {
    if (drawing) return;
    setDrawing(true);
    try {
      const settings = {
        ...event.settings,
        lotteryPrizeType: prizeType,
        lotteryPrizeCustomText: customText,
        lotteryAllowRepeatWinners: allowRepeatWinners,
      };
      const round = await drawWinner(eventId, djId, settings);
      toast.success(`Winner: ${lotteryWinnerLabel(round)}!`);
    } catch (error: any) {
      console.error('Lottery draw error:', error);
      if (error.message === 'No eligible participants for lottery draw') {
        toast.error('No eligible participants for lottery draw');
      } else {
        toast.error('Failed to draw winner');
      }
    } finally {
      setDrawing(false);
    }
  }, [drawing, event.settings, prizeType, customText, allowRepeatWinners, eventId, djId]);

  const handleDrawRef = useRef(handleDraw);
  useEffect(() => {
    handleDrawRef.current = handleDraw;
  }, [handleDraw]);

  useEffect(() => {
    if (!isRunning || mode !== 'recurring') {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setSecondsRemaining(0);
      return;
    }

    if (!nextDrawTimeRef.current || nextDrawTimeRef.current <= Date.now()) {
      nextDrawTimeRef.current = Date.now() + intervalMinutes * 60000;
    }

    timerRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((nextDrawTimeRef.current - Date.now()) / 1000));
      setSecondsRemaining(remaining);

      if (remaining <= 0) {
        nextDrawTimeRef.current = Date.now() + intervalMinutes * 60000;
        handleDrawRef.current();
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRunning, mode, intervalMinutes]);

  const saveSettings = async (enabled: boolean) => {
    const updatedSettings = {
      ...event.settings,
      lotteryEnabled: enabled,
      lotteryMode: mode,
      lotteryIntervalMinutes: intervalMinutes,
      lotteryPrizeType: prizeType,
      lotteryPrizeCustomText: customText,
      lotteryAllowRepeatWinners: allowRepeatWinners,
    };

    try {
      const updatedEvent = await updateEvent(event.id, { settings: updatedSettings });
      setEvent(updatedEvent);
    } catch {
      toast.error('Failed to save lottery settings');
    }
  };

  const handleStart = async () => {
    setIsRunning(true);
    nextDrawTimeRef.current = Date.now() + intervalMinutes * 60000;
    await saveSettings(true);
    toast.success('Lottery started!');
  };

  const handleStop = async () => {
    setIsRunning(false);
    nextDrawTimeRef.current = 0;
    await saveSettings(false);
    toast.success('Lottery stopped');
  };

  const formatTime = (totalSeconds: number) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (isRunning) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
          </span>
          <span className="text-sm font-medium text-green-700 dark:text-green-300">Lottery Active</span>
          {mode === 'recurring' && secondsRemaining > 0 && (
            <span className="ml-auto flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
              <Clock className="w-4 h-4" />
              {formatTime(secondsRemaining)}
            </span>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleDraw}
            disabled={drawing}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            <Ticket className="w-4 h-4" />
            {drawing ? 'Drawing...' : 'Draw Now'}
          </button>
          <button
            onClick={handleStop}
            className="flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50"
          >
            <Square className="w-4 h-4" />
            Stop
          </button>
        </div>

        {rounds.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
              <Trophy className="w-4 h-4" />
              History ({rounds.length} {rounds.length === 1 ? 'round' : 'rounds'})
            </h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {rounds.map((round) => (
                <div
                  key={round.id}
                  className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-sm"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 dark:text-gray-500 font-mono text-xs">
                      #{round.roundNumber}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {lotteryWinnerLabel(round)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <Users className="w-3 h-3" />
                    {round.participantCount}
                    <span>{new Date(round.drawnAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Mode</label>
          <div className="flex gap-2">
            <button
              onClick={() => setMode('one_time')}
              className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                mode === 'one_time'
                  ? 'border-primary bg-primary/10 text-primary font-medium'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              One-time
            </button>
            <button
              onClick={() => setMode('recurring')}
              className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                mode === 'recurring'
                  ? 'border-primary bg-primary/10 text-primary font-medium'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <div className="flex items-center justify-center gap-1">
                <RotateCcw className="w-3 h-3" />
                Recurring
              </div>
            </button>
          </div>
        </div>

        {mode === 'recurring' && (
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
              Draw every (minutes)
            </label>
            <input
              type="number"
              min={1}
              max={60}
              value={intervalMinutes}
              onChange={(e) => setIntervalMinutes(Math.min(60, Math.max(1, parseInt(e.target.value) || 1)))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
          </div>
        )}

        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Prize</label>
          <div className="grid grid-cols-2 gap-2">
            {PRIZE_OPTIONS.map((opt) => (
              <button
                key={opt.type}
                onClick={() => setPrizeType(opt.type)}
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border ${
                  prizeType === opt.type
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <span>{opt.icon}</span>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {(prizeType === 'custom' || prizeType === 'shoutout') && (
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
              {prizeType === 'shoutout' ? 'Shoutout message' : 'Prize description'}
            </label>
            <input
              type="text"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder={prizeType === 'shoutout' ? 'e.g., "Congrats to our winner!"' : 'e.g., "Free drink at the bar"'}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
          </div>
        )}

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Allow repeat winners</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Same attendee can win multiple rounds</p>
          </div>
          <button
            type="button"
            onClick={() => setAllowRepeatWinners(!allowRepeatWinners)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              allowRepeatWinners ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                allowRepeatWinners ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      <button
        onClick={handleStart}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 font-medium"
      >
        <Play className="w-4 h-4" />
        Start Lottery
      </button>

      {rounds.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
            <Trophy className="w-4 h-4" />
            Previous Rounds ({rounds.length})
          </h3>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {rounds.map((round) => (
              <div
                key={round.id}
                className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-sm"
              >
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 dark:text-gray-500 font-mono text-xs">
                    #{round.roundNumber}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {lotteryWinnerLabel(round)}
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(round.drawnAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
